import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { comics, genreList, searchHotWords } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";
import ComicFilters from "@/components/feature/ComicFilters";
import EmptyState from "@/components/feature/EmptyState";
import Pagination, { Breadcrumb } from "@/components/feature/Pagination";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  filterComics,
  paginate,
  PAGE_SIZE,
  type SortKey,
  type StatusKey,
} from "@/utils/comicFilter";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") || "";
  const [input, setInput] = useState(keyword);

  const activeGenres = (params.get("genre") || "")
    .split(",")
    .map((g) => g.trim())
    .filter(Boolean);
  const status = (params.get("status") as StatusKey) || "all";
  const sort = (params.get("sort") as SortKey) || "update";
  const page = Math.max(1, Number(params.get("page")) || 1);

  useDocumentTitle(
    keyword ? `${keyword} 的搜索结果 - 老司机漫画` : "搜索结果 - 老司机漫画",
    `在老司机漫画搜索“${keyword}”相关的漫画作品与作者。`,
  );

  const results = useMemo(() => {
    if (!keyword.trim()) return [];
    return filterComics(comics, { genres: activeGenres, status, sort, keyword });
  }, [keyword, activeGenres, status, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageList = paginate(results, page);
  const recommendations = useMemo(
    () => [...comics].sort((a, b) => b.favorites - a.favorites).slice(0, 5),
    [],
  );

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    });
    setParams(next);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    update({ q: input.trim(), page: null });
  };

  const toggleGenre = (genre: string) => {
    const set = new Set(activeGenres);
    if (set.has(genre)) set.delete(genre);
    else set.add(genre);
    update({ genre: Array.from(set).join(","), page: null });
  };

  const reset = () => {
    setInput("");
    setParams(new URLSearchParams());
  };

  const hasKeyword = keyword.trim().length > 0;

  return (
    <main className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "搜索" }]} />

      <header className="mt-4 mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          搜索漫画
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">
          输入漫画名称或作者名，快速定位感兴趣的作品。
        </p>
      </header>

      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="搜索漫画名 / 作者，例如：烈火战魂、陈烽"
            className="w-full h-12 pl-11 pr-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <button
          type="submit"
          className="h-12 px-8 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      {!hasKeyword && (
        <div className="mt-5">
          <p className="text-sm text-foreground-500 mb-2">
            <i className="ri-fire-line mr-1 text-primary-500"></i>
            大家都在搜：
          </p>
          <div className="flex flex-wrap gap-2">
            {searchHotWords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => {
                  setInput(word);
                  update({ q: word, page: null });
                }}
                className="px-3 h-9 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      )}

      {hasKeyword && (
        <>
          <div className="mt-6 flex items-center flex-wrap gap-2 text-sm">
            <span className="text-foreground-500">关键词：</span>
            <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 font-medium">
              {keyword}
            </span>
            <span className="text-foreground-500">
              共匹配 <strong className="text-primary-600 font-semibold">{results.length}</strong> 部作品
            </span>
          </div>

          <div className="mt-5">
            <ComicFilters
              genres={genreList}
              activeGenres={activeGenres}
              onToggleGenre={toggleGenre}
              status={status}
              onStatusChange={(s) => update({ status: s === "all" ? null : s, page: null })}
              sort={sort}
              onSortChange={(s) => update({ sort: s === "update" ? null : s, page: null })}
              onReset={() => update({ genre: null, status: null, sort: null, page: null })}
            />
          </div>

          <div className="mt-8">
            {pageList.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {pageList.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
                <Pagination
                  current={Math.min(page, totalPages)}
                  total={totalPages}
                  onChange={(p) => {
                    update({ page: p === 1 ? null : String(p) });
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </>
            ) : (
              <EmptyState
                title={`没有找到与“${keyword}”相关的漫画`}
                desc="换个关键词试试，或者看看下方为你在热门作品中的推荐。搜索与筛选结果均为前端演示数据。"
                onReset={reset}
                resetLabel="清空搜索"
                recommendations={recommendations}
              />
            )}
          </div>
        </>
      )}

      {!hasKeyword && (
        <div className="mt-10">
          <h2 className="font-heading font-bold text-lg text-foreground-950 mb-5">
            <i className="ri-fire-line mr-1.5 text-primary-500"></i>
            人气推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {recommendations.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      )}

      <p className="mt-8 text-xs text-foreground-400">
        <i className="ri-information-line mr-1"></i>
        搜索结果为前端演示数据，仅用于界面展示，不代表真实作品收录情况。
        <Link to="/category" className="ml-1 text-accent-600 hover:underline cursor-pointer">
          直接浏览分类
        </Link>
      </p>
    </main>
  );
}